/**
 * Resolves ruleset/mandate IDs (e.g., "4-1-2", "rs-4-1-002") to display names
 * (e.g., "MN 144G.71 - Medication Management").
 *
 * Uses ListRulesets query — cached by Apollo.
 * Returns a resolver function for mandate badges and detail panels.
 *
 * DRR-117: mandate_ref display resolution for T1 audit trail.
 */

import { useMemo } from 'react';
import { useListRulesetsQuery } from '@/generated/graphql';

/**
 * Build a lookup map from rulesetId → display name.
 * Returns a resolver function: (rulesetId: string) => string
 *
 * Usage:
 *   const resolve = useResolveRulesetName();
 *   resolve("4-1-2")      // → "MN 144G.71 - Medication Management"
 *   resolve("rs-4-1-002") // → "MN 144G.72 - Delegation"
 *   resolve("unknown")    // → "unknown" (passthrough)
 */
export function useResolveRulesetName(): (rulesetId: string | null | undefined) => string {
  // Skip query if we're not in a valid Apollo context (prevents crashes during SSR or early render)
  const { data, error } = useListRulesetsQuery({
    fetchPolicy: 'cache-first',
    // Don't throw on network errors - gracefully degrade to showing raw IDs
    errorPolicy: 'ignore',
  });

  const lookup = useMemo(() => {
    const map = new Map<string, string>();
    // If query failed or no data, return empty map (will passthrough raw IDs)
    if (error || !data?.ruleSets) return map;

    for (const rs of data.ruleSets) {
      if (rs.rulesetId && rs.name) {
        // Store both full ID and short form (without rs- prefix)
        map.set(rs.rulesetId, rs.name);
        // Also map short form: "rs-4-1-002" → "4-1-2"
        const shortForm = rs.rulesetId.replace(/^rs-/, '').replace(/-0+/g, '-').replace(/-$/, '');
        if (shortForm !== rs.rulesetId) {
          map.set(shortForm, rs.name);
        }
      }
    }
    return map;
  }, [data, error]);

  return (rulesetId: string | null | undefined): string => {
    if (!rulesetId) return '(no mandate)';
    return lookup.get(rulesetId) ?? rulesetId;
  };
}
